// ═══════════════════════════════════════════════════════════════════════════
// Bodegas
//
// El operador: "Debe soportar múltiples bodegas. Un inventario único con la
// opción de mercancía en tránsito."
//
// Las dos cosas a la vez, que parecen reñidas y no lo son: arriba el TOTAL por
// producto —que es el inventario único y no cambia al mover nada de sitio— y
// debajo dónde está cada botella.
//
// ── EL TRÁNSITO ES UNA BODEGA ──────────────────────────────────────────────
//
// Mandar mercancía = trasladarla a «En tránsito». Recibirla = trasladarla
// desde ahí. No hay un estado «enviado» que consultar: si está en esa bodega,
// está en camino. La decisión y su porqué están en la migración 0057.
//
// Por eso esta pantalla no tiene un formulario de «envíos» aparte del de
// traslados: sería el mismo formulario dos veces.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { leerBodegas, leerExistenciasPorBodega, guardarBodega, trasladar,
         leerTraslados, hayTransporte } from "../datos/bodegas.js";
import { puedeMembresia } from "../auth/permisos.js";
import { Card, Label, NumInput, SectionTitle } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";

const TIPOS = [
  ["bodega",           "Bodega"],
  ["punto_venta",      "Punto de venta"],
  ["sala_degustacion", "Sala de degustación"],
  ["vehiculo",         "Vehículo"],
  ["transito",         "En tránsito"],
];
const etiquetaTipo = (t) => {
  const par = TIPOS.find(x => x[0] === t);
  return par ? par[1] : t;          // un tipo nuevo se enseña tal cual, no vacío
};

export function Bodegas({ products, membresia }) {
  const tenantId = membresia?.tenant_id;
  const puedeAdministrar = puedeMembresia(membresia, "administrar_bodegas");

  const [bodegas, setBodegas] = useState([]);
  const [existencias, setExistencias] = useState({ porBodega: new Map(), total: new Map() });
  const [traslados, setTraslados] = useState([]);
  const [estado, setEstado] = useState("cargando");
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(null);
  const [mov, setMov] = useState(null);

  // El catálogo llega con el id LEGADO; las existencias con el de Postgres.
  // Este mapa es el puente, y se arma una vez.
  const porPg = useMemo(() => {
    const m = new Map();
    for (const p of products || []) if (p._pgId) m.set(p._pgId, p);
    return m;
  }, [products]);

  const recargar = useCallback(async () => {
    if (!tenantId) return;
    setEstado("cargando");
    try {
      const [b, e, t] = await Promise.all([
        leerBodegas(tenantId), leerExistenciasPorBodega(tenantId), leerTraslados(tenantId),
      ]);
      setBodegas(b); setExistencias(e); setTraslados(t);
      setEstado("listo"); setError(null);
    } catch (err) {
      setEstado("error"); setError(err.message);
    }
  }, [tenantId]);

  useEffect(() => { recargar(); }, [recargar]);

  const totalDe = (bodegaId) => {
    const m = existencias.porBodega.get(bodegaId);
    if (!m) return 0;
    let s = 0; for (const v of m.values()) s += v;
    return s;
  };

  // Las sedes que ya existen, para sugerirlas al escribir una nueva: si cada
  // quien teclea «Valle de San Vicente» a su manera, dos bodegas del mismo
  // domicilio parecerán de domicilios distintos y el sistema dirá que hay
  // transporte donde no lo hay.
  const sedes = useMemo(
    () => [...new Set(bodegas.map(b => b.sede).filter(Boolean))].sort(),
    [bodegas]);

  const enTransito = bodegas.filter(b => b.type === "transito").reduce((s, b) => s + totalDe(b.id), 0);
  const totalGeneral = [...existencias.total.values()].reduce((s, v) => s + v, 0);

  // ── Alta y edición ───────────────────────────────────────────────────────
  const guardar = async () => {
    try {
      await guardarBodega(tenantId, editando);
      setEditando(null);
      await recargar();
    } catch (e) { alert("❌ " + e.message); }
  };

  // ── Traslado ─────────────────────────────────────────────────────────────
  const hacerTraslado = async () => {
    const { productoLegacy, origen, destino, cantidad } = mov || {};
    if (!productoLegacy || !origen || !destino || !(cantidad > 0)) return;
    try {
      const r = await trasladar(tenantId, mov);
      setMov(null);
      await recargar();
      // El aviso del servidor se ENSEÑA. Es el que dice que el origen quedó en
      // negativo, y tragárselo dejaría a alguien creyendo que movió lo que
      // tenía cuando movió lo que no.
      if (r?.aviso) alert("⚠️ " + r.aviso);
      else alert(`✅ ${r.qty} × ${productoNombre(productoLegacy)} · ${r.origen} → ${r.destino}` +
                 (r.en_transito ? "\n\nQueda EN TRÁNSITO hasta que se reciba en su destino." : ""));
    } catch (e) { alert("❌ " + e.message); }
  };

  const productoNombre = (legacy) =>
    (products || []).find(p => p.id === legacy)?.name || legacy;

  if (!tenantId) return null;

  if (estado === "error") return (
    <Card style={{ padding: 16 }}>
      <div style={{ color: "#c97070", fontSize: 13 }}>No se pudieron leer las bodegas: {error}</div>
      <button onClick={recargar} style={{ ...btnSecondary, marginTop: 10 }}>Reintentar</button>
    </Card>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                    flexWrap: "wrap", gap: 12 }}>
        <SectionTitle sub={`${bodegas.length} bodega${bodegas.length === 1 ? "" : "s"} · ` +
                           `${totalGeneral} botella${totalGeneral === 1 ? "" : "s"} en total`}>
          Bodegas
        </SectionTitle>
        {puedeAdministrar && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setMov({ productoLegacy: "", origen: "", destino: "", cantidad: 0, nota: "" })}
              style={btnSecondary}>⇄ Trasladar</button>
            <button onClick={() => setEditando({ id: null, nombre: "", tipo: "bodega", esSalida: false, activa: true })}
              style={btnPrimary}>+ Nueva bodega</button>
          </div>
        )}
      </div>

      {/* ── El inventario único, arriba ──────────────────────────────────────
          Se enseña primero a propósito: es el número que cuadra con lo que la
          vinícola cree tener, y el desglose de abajo explica dónde está. Al
          revés se lee como si hubiera varios inventarios. */}
      <Card style={{ marginBottom: 14, padding: "14px 16px", display: "flex",
                     gap: 24, flexWrap: "wrap", alignItems: "baseline" }}>
        <div>
          <div style={{ fontSize: 10, color: "#666", letterSpacing: 1 }}>INVENTARIO ÚNICO</div>
          <div style={{ fontSize: 24, color: "#c9a96e", fontWeight: 600 }}>{totalGeneral}</div>
          <div style={{ fontSize: 10, color: "#666" }}>botellas, en todas las bodegas</div>
        </div>
        {enTransito !== 0 && (
          <div>
            <div style={{ fontSize: 10, color: "#666", letterSpacing: 1 }}>EN TRÁNSITO</div>
            <div style={{ fontSize: 24, color: "#70a0c9", fontWeight: 600 }}>{enTransito}</div>
            <div style={{ fontSize: 10, color: "#666" }}>salieron y no han llegado</div>
          </div>
        )}
        <div style={{ fontSize: 11, color: "#666", flex: 1, minWidth: 220, lineHeight: 1.6 }}>
          Mover mercancía entre bodegas no cambia el total: el vino sigue siendo
          de la vinícola mientras viaja.
        </div>
      </Card>

      {/* ── Cada bodega ──────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {bodegas.map(b => {
          const detalle = existencias.porBodega.get(b.id);
          const conAlgo = [...(detalle?.entries() || [])]
            .filter(([, q]) => q !== 0)
            .sort((x, y) => y[1] - x[1]);
          return (
            <Card key={b.id} style={{ padding: 14, opacity: b.is_active ? 1 : 0.55 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{b.name}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                                   background: b.type === "transito" ? "#15202a" : "#1a1a1a",
                                   color: b.type === "transito" ? "#70a0c9" : "#999" }}>
                      {etiquetaTipo(b.type)}
                    </span>
                    {b.sede ? (
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                                     background: "#141a14", color: "#8aa88a" }}>{b.sede}</span>
                    ) : (
                      <span title="Sin sede no se puede saber si un traslado involucra transporte"
                        style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                                 background: "#1a1a1a", color: "#777" }}>sin sede</span>
                    )}
                    {b.is_outbound && (
                      <span title="De aquí se descuenta el vino al levantar una remisión"
                        style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                                 background: "#1f1a0d", color: "#c9a96e" }}>de salida</span>
                    )}
                    {!b.is_active && (
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                                     background: "#2a1515", color: "#c97070" }}>inactiva</span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 20, fontWeight: 600,
                                color: totalDe(b.id) < 0 ? "#facc15" : "#e8e0d4" }}>
                    {totalDe(b.id)}
                  </div>
                  {puedeAdministrar && (
                    <button onClick={() => setEditando({
                      id: b.id, nombre: b.name, tipo: b.type,
                      esSalida: b.is_outbound, activa: b.is_active })}
                      style={{ ...btnSecondary, padding: "2px 10px", fontSize: 11, marginTop: 4 }}>✏️</button>
                  )}
                </div>
              </div>

              {conAlgo.length > 0 && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #1a1a1a",
                              fontSize: 11, color: "#888" }}>
                  {conAlgo.slice(0, 6).map(([pg, q]) => (
                    <div key={pg} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {porPg.get(pg)?.name || "producto fuera del catálogo"}
                      </span>
                      <span style={{ color: q < 0 ? "#facc15" : "#aaa", flexShrink: 0 }}>{q}</span>
                    </div>
                  ))}
                  {conAlgo.length > 6 && (
                    <div style={{ color: "#555", marginTop: 4 }}>y {conAlgo.length - 6} producto(s) más</div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* ── Traslados recientes ──────────────────────────────────────────── */}
      {traslados.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <SectionTitle sub="cada uno son dos asientos que suman cero">Traslados recientes</SectionTitle>
          <Card style={{ padding: 12 }}>
            {traslados.slice(0, 12).map(t => {
              const nombre = (id) => bodegas.find(b => b.id === id)?.name || "—";
              return (
                <div key={t.id} style={{ display: "flex", gap: 10, flexWrap: "wrap",
                                         padding: "6px 0", borderBottom: "1px solid #141414", fontSize: 12 }}>
                  <span style={{ color: "#666", flexShrink: 0 }}>
                    {new Date(t.fecha).toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}
                  </span>
                  <span style={{ flex: 1, minWidth: 150 }}>
                    {porPg.get(t.productoPg)?.name || "producto"}
                  </span>
                  <span style={{ color: "#888" }}>{nombre(t.origen)} → {nombre(t.destino)}</span>
                  {(() => {
                    const con = hayTransporte(bodegas.find(b => b.id === t.origen),
                                              bodegas.find(b => b.id === t.destino));
                    return con === true
                      ? <span title="Cruzó de sede" style={{ color: "#70a0c9" }}>🚚</span>
                      : null;   // ni «no» ni «no se sabe» necesitan un icono
                  })()}
                  <span style={{ color: "#c9a96e", fontWeight: 600 }}>{t.cantidad}</span>
                  {!t.completo && (
                    <span title="A este traslado le falta uno de sus dos asientos"
                      style={{ fontSize: 10, color: "#c97070" }}>incompleto</span>
                  )}
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* ── Formulario de bodega ─────────────────────────────────────────── */}
      {editando && (
        <Modal titulo={editando.id ? "Editar bodega" : "Nueva bodega"} onCerrar={() => setEditando(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14 }}>
            <div>
              <Label>Nombre</Label>
              <input value={editando.nombre} autoFocus
                onChange={e => setEditando(v => ({ ...v, nombre: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <Label>Tipo</Label>
              <select value={editando.tipo} onChange={e => setEditando(v => ({ ...v, tipo: e.target.value }))}
                style={selectStyle}>
                {TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <Label>Sede</Label>
              <input value={editando.sede || ""} list="sedes-conocidas"
                onChange={e => setEditando(v => ({ ...v, sede: e.target.value }))}
                placeholder="Valle de San Vicente" style={inputStyle} />
              <datalist id="sedes-conocidas">
                {sedes.map(s => <option key={s} value={s} />)}
              </datalist>
              <div style={{ fontSize: 11, color: "#666", marginTop: 4, lineHeight: 1.6 }}>
                Dónde está físicamente. Dos bodegas con la MISMA sede están en el
                mismo domicilio: moverse entre ellas no involucra transporte.
                Si se deja en blanco, de sus traslados no se podrá decir si lo hubo.
              </div>
            </div>
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 18, flexWrap: "wrap" }}>
            <label style={{ fontSize: 12, color: "#aaa", display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={!!editando.esSalida}
                onChange={e => setEditando(v => ({ ...v, esSalida: e.target.checked }))} />
              De salida
            </label>
            <label style={{ fontSize: 12, color: "#aaa", display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={editando.activa !== false}
                onChange={e => setEditando(v => ({ ...v, activa: e.target.checked }))} />
              Activa
            </label>
          </div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 8, lineHeight: 1.6 }}>
            «De salida» es de donde se descuenta el vino al levantar una remisión.
            Solo puede haber una: al marcar esta, las demás se desmarcan.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <button onClick={() => setEditando(null)} style={btnSecondary}>Cancelar</button>
            <button onClick={guardar} style={btnPrimary}>💾 Guardar</button>
          </div>
        </Modal>
      )}

      {/* ── Formulario de traslado ───────────────────────────────────────── */}
      {mov && (
        <Modal titulo="Trasladar mercancía" onCerrar={() => setMov(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Label>Producto</Label>
              <select value={mov.productoLegacy} onChange={e => setMov(v => ({ ...v, productoLegacy: e.target.value }))}
                style={selectStyle}>
                <option value="">— elige —</option>
                {(products || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Desde</Label>
              <select value={mov.origen} onChange={e => setMov(v => ({ ...v, origen: e.target.value }))}
                style={selectStyle}>
                <option value="">— elige —</option>
                {bodegas.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Hacia</Label>
              <select value={mov.destino} onChange={e => setMov(v => ({ ...v, destino: e.target.value }))}
                style={selectStyle}>
                <option value="">— elige —</option>
                {bodegas.filter(b => b.is_active).map(b => (
                  <option key={b.id} value={b.id}>{b.name}{b.type === "transito" ? " (en tránsito)" : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Botellas</Label>
              <NumInput value={mov.cantidad} onChange={v => setMov(x => ({ ...x, cantidad: v }))}
                style={inputStyle} integer min={0} />
            </div>
            <div>
              <Label>Nota</Label>
              <input value={mov.nota} onChange={e => setMov(v => ({ ...v, nota: e.target.value }))}
                placeholder="Envío a Ensenada" style={inputStyle} />
            </div>
          </div>

          {/* ¿Lleva camión? Se dice ANTES de trasladar, no después: es la
              diferencia entre mover una tarima a la nave de al lado y mandar
              un envío a trescientos kilómetros, y quien lo captura necesita
              verla mientras decide, no en el acuse. */}
          {mov.origen && mov.destino && mov.origen !== mov.destino && (() => {
            const o = bodegas.find(b => b.id === mov.origen);
            const d = bodegas.find(b => b.id === mov.destino);
            const t = hayTransporte(o, d);
            if (t === null) return (
              <div style={{ fontSize: 12, marginTop: 12, color: "#8a7a4a" }}>
                No se puede saber si involucra transporte: falta declarar la sede
                de {!o?.sede && !d?.sede ? "las dos bodegas" : `«${(!o?.sede ? o : d)?.name}»`}.
              </div>
            );
            return (
              <div style={{ fontSize: 12, marginTop: 12, color: t ? "#70a0c9" : "#8aa88a" }}>
                {t ? `🚚 Involucra transporte: de ${o.sede} a ${d.sede}.`
                   : `Dentro de la misma sede (${o.sede}): sin transporte.`}
              </div>
            );
          })()}

          {/* Cuánto hay donde se va a sacar. Sin esto hay que salir a mirarlo
              a otra pantalla y volver, que es cuando se teclea de más. */}
          {mov.origen && mov.productoLegacy && (() => {
            const pg = (products || []).find(p => p.id === mov.productoLegacy)?._pgId;
            const hay = existencias.porBodega.get(mov.origen)?.get(pg) || 0;
            return (
              <div style={{ fontSize: 12, marginTop: 12,
                            color: mov.cantidad > hay ? "#facc15" : "#888" }}>
                En esa bodega hay <b>{hay}</b>.
                {mov.cantidad > hay && " Se trasladará igual y quedará en negativo: eso dice que faltan entradas por registrar."}
              </div>
            );
          })()}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <button onClick={() => setMov(null)} style={btnSecondary}>Cancelar</button>
            <button onClick={hacerTraslado} style={btnPrimary}>⇄ Trasladar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** Ventana modal, con la barra de botones que sí cabe en un teléfono. */
function Modal({ titulo, onCerrar, children }) {
  return (
    <div onClick={onCerrar}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000,
               display: "flex", alignItems: "flex-start", justifyContent: "center",
               overflow: "auto", padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#0f0f0f", border: "1px solid #222", borderRadius: 10,
                 padding: "clamp(16px, 5vw, 26px)", width: "100%", maxWidth: 560, marginTop: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{titulo}</div>
        {children}
      </div>
    </div>
  );
}
